import { z } from "zod";

/**
 * Shared DTO cho Global Admin Search (US-AE1.F5-0004 / @UC9004).
 * Một nguồn sự thật cho api (S01 — tra cứu + redaction theo vai) và
 * admin web (S03 — render + deep-link), tránh drift shape ở 2 nơi (DRY).
 *
 * BẢO MẬT (invariants):
 * - C2: field PII (email/phone) và tiền (balanceMinor/currency) là OPTIONAL —
 *   backend redact theo vai → contract CHỈ cho phép vắng mặt, KHÔNG ép lộ.
 * - Cursor là opaque string (không nhúng PII) — đối xứng common/pagination/cursor.
 * - KHÔNG hardcode secret / giá trị PII mẫu.
 *
 * Envelope khớp quy ước list của repo: `{ data, pageInfo: { hasNext, nextCursor } }`
 * (common/pagination/cursor.ts · admin-members list). KHÔNG dùng {items, nextCursor}.
 *
 * Limit (index §6b DoR đã chốt): default 20, max 50.
 */

/** Max page-size đã chốt (index §6b). Default (20) do backend áp khi vắng. */
export const ADMIN_SEARCH_MAX_LIMIT = 50;

/** Độ dài tối đa của `q` — chặn input phình to (DoS / abuse) tại biên (M3). 256 dư cho email/uuid/tên. */
export const ADMIN_SEARCH_MAX_QUERY_LENGTH = 256;

/** Query string: `q` bắt buộc (min 1, max 256), cursor opaque optional, limit ∈ [1,50] optional. */
export const adminSearchQuerySchema = z.object({
  q: z.string().min(1).max(ADMIN_SEARCH_MAX_QUERY_LENGTH),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(ADMIN_SEARCH_MAX_LIMIT).optional(),
});

export type AdminSearchQuery = z.infer<typeof adminSearchQuerySchema>;

/** Các loại thực thể tra cứu (index §6b DoR). */
export const adminSearchResultTypeSchema = z.enum([
  "user",
  "group",
  "fund",
  "transaction",
  "audit",
]);

export type AdminSearchResultType = z.infer<typeof adminSearchResultTypeSchema>;

/** User: id + tên hiển thị; email/phone OPTIONAL (có thể bị redaction theo vai). */
const userResultSchema = z.object({
  type: z.literal("user"),
  id: z.string(),
  displayName: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
});

/** Group: id + tên (đủ deep-link). */
const groupResultSchema = z.object({
  type: z.literal("group"),
  id: z.string(),
  name: z.string().optional(),
});

/** Fund: id + tên; balanceMinor/currency OPTIONAL (ẩn với một số vai). */
const fundResultSchema = z.object({
  type: z.literal("fund"),
  id: z.string(),
  name: z.string().optional(),
  balanceMinor: z.string().optional(),
  currency: z.string().optional(),
});

/** Transaction: id + field tối thiểu để deep-link (groupId/fundId, occurredAt). */
const transactionResultSchema = z.object({
  type: z.literal("transaction"),
  id: z.string(),
  groupId: z.string().optional(),
  fundId: z.string().optional(),
  occurredAt: z.string().optional(),
});

/** Audit: id + action/entity tối thiểu để deep-link. */
const auditResultSchema = z.object({
  type: z.literal("audit"),
  id: z.string(),
  action: z.string().optional(),
  entity: z.string().optional(),
  occurredAt: z.string().optional(),
});

/** Discriminated union theo `type` — mỗi item có id + đủ field deep-link. */
export const adminSearchResultItemSchema = z.discriminatedUnion("type", [
  userResultSchema,
  groupResultSchema,
  fundResultSchema,
  transactionResultSchema,
  auditResultSchema,
]);

export type AdminSearchResultItem = z.infer<typeof adminSearchResultItemSchema>;

/** Envelope khớp quy ước repo `{ data, pageInfo }` (cursor opaque, hasNext). */
export const adminSearchResponseSchema = z.object({
  data: z.array(adminSearchResultItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export type AdminSearchResponse = z.infer<typeof adminSearchResponseSchema>;
